import * as React from 'react';
import { useUser } from '@modules/user';
import type { Comment, Entity } from '@modules/build-hub-comment/types';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import {
    decodeMessage,
    toEntities,
} from '@modules/build-hub-comment/utils/comment-parser';
import { getInitials } from '@modules/build-hub/utils/get-initials';
import { useBuildHubData } from '@modules/build-hub/hooks/use-build-hub-data';
import { timeToNow } from '@modules/date-time/util/utc-date-to-local-timezone';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { MoreVertical, Link as LinkIcon, MailOpenIcon } from 'lucide-react';
import { useCommentMarkAsUnread } from '@modules/build-hub-comment/hooks/use-comment-mark-as-unread';
import { toast } from 'sonner';
import { useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { useParams } from '@remix-run/react';

interface CommentViewerProps {
    comment: Comment;
    documentId: string;
    onMarkAsUnread?: (commentId: number) => void;
    isHighlighted?: boolean;
    setRef?: (id: number, el: HTMLDivElement | null) => void;
}

export const CommentViewer = ({
    comment,
    documentId,
    onMarkAsUnread,
    isHighlighted = false,
    setRef,
}: CommentViewerProps) => {
    const user = useUser();
    const { buildHub } = useBuildHubData();
    const { markAsUnread } = useCommentMarkAsUnread();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const params = useParams();

    const handleCopyLink = React.useCallback(
        (commentId: number) => {
            if (!params.boardId) {
                toast.error('Unable to copy link in this context');
                setIsPopoverOpen(false);
                return;
            }

            // Construct URL: /app/project/{id}/design-boards/{boardId}?item={pinId}&commentID={commentId}
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/app/project/${buildHub.urlSegment}/design-boards/${params.boardId}?item=${documentId}&commentID=${commentId}`;

            navigator.clipboard
                .writeText(url)
                .then(() => {
                    toast.success('Link copied to clipboard');
                })
                .catch(() => {
                    toast.error('Failed to copy link');
                });

            setIsPopoverOpen(false);
        },
        [buildHub.urlSegment, params.boardId, documentId]
    );

    const handleMarkAsUnread = React.useCallback(() => {
        // Call backend mutation with comment ID
        markAsUnread(documentId, String(comment.id));
        // Update UI immediately for visual feedback
        onMarkAsUnread?.(comment.id);
        setIsPopoverOpen(false);
    }, [markAsUnread, documentId, onMarkAsUnread, comment.id]);

    const { id, message, createdAt, author, mentions, files } = comment;
    const isMine = author.id === user.ID;
    const messageEntities = toEntities(
        mentions ?? [],
        files ?? [],
        buildHub.owner
    );

    return (
        <div
            key={id}
            ref={(el) => setRef?.(id, el)}
            className={cn(
                'flex items-start gap-3 rounded-lg py-2 pl-1 pr-2 transition-colors focus-within:bg-gray-50 hover:bg-gray-50',
                isPopoverOpen && 'bg-gray-50',
                isHighlighted && 'animate-comment-highlight'
            )}
        >
            <Avatar size="square40">
                <AvatarImage
                    src={author.avatar ?? undefined}
                    alt={author.name}
                    fit="cover"
                />
                <AvatarFallback size="md">
                    {author.name ? getInitials(author.name) : '?'}
                </AvatarFallback>
            </Avatar>

            <div className={`relative w-full text-sm text-gray-900`}>
                <div className="flex flex-row items-center justify-between gap-2">
                    <div className="flex flex-row items-center gap-2">
                        <div className="text-sm font-medium">{author.name}</div>
                        {createdAt && (
                            <div className={`text-xs text-gray-400`}>
                                {timeToNow(createdAt)}
                            </div>
                        )}
                    </div>

                    <Popover
                        open={isPopoverOpen}
                        onOpenChange={setIsPopoverOpen}
                    >
                        <PopoverTrigger asChild>
                            <button
                                className="flex size-6 items-center justify-center rounded hover:bg-gray-100"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MoreVertical className="size-4 text-gray-400" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="end"
                            paddings="xsmall"
                            variant="white"
                            background="white"
                        >
                            <div className="flex min-w-60 flex-col space-y-2">
                                <button
                                    className="group flex cursor-pointer items-center justify-start space-x-2 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-gray-100"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkAsUnread();
                                    }}
                                >
                                    <MailOpenIcon className="mr-2 h-4 w-4" />
                                    Mark as unread
                                </button>
                                {params.boardId && (
                                    <>
                                        <div className="h-px w-full bg-gray-200" />
                                        <button
                                            className="group flex cursor-pointer items-center justify-start space-x-2 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-gray-100"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopyLink(id);
                                            }}
                                        >
                                            <LinkIcon className="mr-2 h-4 w-4" />
                                            Copy link
                                        </button>
                                    </>
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="whitespace-pre-wrap text-sm">
                    {renderMessageWithMentions(
                        message,
                        messageEntities,
                        isMine
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Render a message with mentions styled in lighter blue.
 * Uses decodeMessage + entities so multi-word mentions render correctly.
 */
function renderMessageWithMentions(
    rawMessage: string,
    entities: Entity[],
    isMine: boolean
) {
    const decoded = decodeMessage(rawMessage, entities);
    const sortedEntities = [...entities].sort(
        (a, b) => b.label.length - a.label.length
    );

    let parts: (string | JSX.Element)[] = [decoded];

    for (const ent of sortedEntities) {
        const next: (string | JSX.Element)[] = [];
        const needle = `@${ent.label}`;

        parts.forEach((p, i) => {
            if (typeof p === 'string') {
                const split = p.split(needle);
                split.forEach((chunk, j) => {
                    if (chunk) next.push(chunk);
                    if (j < split.length - 1) {
                        next.push(
                            <span
                                key={`${ent.type}:${ent.id}:${i}:${j}`}
                                style={{
                                    color: isMine ? '#60a5fa' : '#2563eb', // lighter blue if mine
                                    fontWeight: 500,
                                }}
                            >
                                {needle}
                            </span>
                        );
                    }
                });
            } else {
                next.push(p);
            }
        });

        parts = next;
    }

    return <>{parts}</>;
}
